import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import ScheduleIcon from '@mui/icons-material/Schedule'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import UndoIcon from '@mui/icons-material/Undo'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import DeleteForeverIcon from '@mui/icons-material/DeleteForever'
import {
  mitigationService,
  MitigationRung,
  MitigationTimeline,
} from '../api/mitigationService'
import { shipmentService } from '../api/adminServices'
import { fmtDateTime } from '../utils/propertyFormat'
import {
  allRungsAttempted,
  getGivenUpGuidance,
  getTimelineGuidance,
  hasOutstandingReprice,
} from '../utils/mitigationOpsUtils'
import { useToast } from './useToast'
import ConfirmDialog from './ConfirmDialog'
import ManualPlacementDialog from './ManualPlacementDialog'

const RUNG_LABELS: Record<MitigationRung, string> = {
  WIDEN_DISPATCH: 'Widen dispatch',
  REPRICE: 'Reprice',
  SHIFT_PICKUP_WINDOW: 'Shift pickup window',
  PREFER_STRONGER_SHIPPER: 'Prefer stronger shipper',
}

const EVENT_TYPE_LABELS: Record<string, string> = {
  LADDER_ENTERED: 'Entered ladder',
  RUNG_ATTEMPTED: 'Rung attempted',
  RUNG_OUTCOME: 'Rung outcome',
  SHIPPER_DECIDED: 'Shipper decided',
  PLACED: 'Placed',
  GAVE_UP: 'Gave up',
  OPS_ACTION: 'Ops action',
}

function rungChipColor(rung?: MitigationRung | null): 'default' | 'primary' | 'secondary' | 'info' {
  if (!rung) return 'default'
  if (rung === 'REPRICE') return 'secondary'
  if (rung === 'WIDEN_DISPATCH') return 'primary'
  return 'info'
}

function urgencyColor(urgency?: string): 'default' | 'warning' | 'error' {
  if (urgency === 'EMERGENCY_URGENT') return 'error'
  if (urgency === 'URGENT') return 'warning'
  return 'default'
}

function formatHours(h?: number): string {
  if (h == null) return '—'
  if (h < 1) return `${Math.round(h * 60)}m`
  return `${h.toFixed(1)}h`
}

interface MitigationTimelineDialogProps {
  shipmentRequestId: string | null
  open: boolean
  onClose: () => void
  onActionComplete?: () => void
}

export default function MitigationTimelineDialog({
  shipmentRequestId,
  open,
  onClose,
  onActionComplete,
}: MitigationTimelineDialogProps) {
  const toast = useToast()
  const queryClient = useQueryClient()

  const [extendHours, setExtendHours] = useState('2')
  const [extendReason, setExtendReason] = useState('')
  const [returnReason, setReturnReason] = useState('')
  const [resetConfirm, setResetConfirm] = useState(false)
  const [returnConfirm, setReturnConfirm] = useState(false)
  const [resetToNewConfirm, setResetToNewConfirm] = useState(false)
  const [placementOpen, setPlacementOpen] = useState(false)

  const { data: timeline, isLoading, error, refetch } = useQuery({
    queryKey: ['mitigation-timeline', shipmentRequestId],
    queryFn: () => mitigationService.getTimeline(shipmentRequestId as string),
    enabled: open && !!shipmentRequestId,
  })

  useEffect(() => {
    if (!open) {
      setExtendHours('2')
      setExtendReason('')
      setReturnReason('')
      setResetConfirm(false)
      setReturnConfirm(false)
      setResetToNewConfirm(false)
      setPlacementOpen(false)
    }
  }, [open])

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['mitigation-timeline', shipmentRequestId] })
    queryClient.invalidateQueries({ queryKey: ['mitigation-at-risk'] })
    queryClient.invalidateQueries({ queryKey: ['mitigation-given-up'] })
    queryClient.invalidateQueries({ queryKey: ['mitigation-funnel'] })
    onActionComplete?.()
  }

  const mitigateMutation = useMutation({
    mutationFn: () => mitigationService.mitigateNow(shipmentRequestId as string),
    onSuccess: () => {
      toast.success('Mitigation tick triggered')
      invalidate()
      refetch()
    },
    onError: (e) => toast.errorFrom(e, 'Failed to run mitigation'),
  })

  const resetMutation = useMutation({
    mutationFn: () => mitigationService.resetLadder(shipmentRequestId as string),
    onSuccess: () => {
      toast.success('Ladder reset — all rungs available again')
      setResetConfirm(false)
      invalidate()
      refetch()
    },
    onError: (e) => toast.errorFrom(e, 'Failed to reset ladder'),
  })

  const extendMutation = useMutation({
    mutationFn: () =>
      mitigationService.extendDeadline(shipmentRequestId as string, {
        hours: parseFloat(extendHours),
        reason: extendReason.trim() || undefined,
      }),
    onSuccess: () => {
      toast.success('Deadline extended')
      setExtendReason('')
      invalidate()
      refetch()
    },
    onError: (e) => toast.errorFrom(e, 'Failed to extend deadline'),
  })

  const applyPriceMutation = useMutation({
    mutationFn: () => mitigationService.applyPriceEscalation(shipmentRequestId as string),
    onSuccess: () => {
      toast.success('Approved price escalation applied')
      invalidate()
      refetch()
    },
    onError: (e) => toast.errorFrom(e, 'Failed to apply price escalation'),
  })

  const returnMutation = useMutation({
    mutationFn: () =>
      mitigationService.returnToMatching(
        shipmentRequestId as string,
        returnReason.trim() || undefined,
      ),
    onSuccess: () => {
      toast.success('Returned to matching')
      setReturnConfirm(false)
      setReturnReason('')
      invalidate()
      refetch()
    },
    onError: (e) => toast.errorFrom(e, 'Failed to return to matching'),
  })

  const resetToNewMutation = useMutation({
    mutationFn: () => shipmentService.resetShipmentRequestToNew(shipmentRequestId as string),
    onSuccess: () => {
      toast.success('Request reset to NEW — shipments and offers dropped')
      setResetToNewConfirm(false)
      invalidate()
      refetch()
    },
    onError: (e) => toast.errorFrom(e, 'Failed to reset to NEW'),
  })

  const outstandingReprice = timeline ? hasOutstandingReprice(timeline) : false
  const isGivenUp = !!timeline?.givenUpAt
  const needsCarrierSourcing =
    timeline &&
    !isGivenUp &&
    allRungsAttempted(timeline.attemptedMitigationRungs) &&
    (timeline.hoursToDeadline ?? Infinity) < 6

  const opsGuidance = timeline
    ? isGivenUp
      ? getGivenUpGuidance()
      : getTimelineGuidance(timeline)
    : null

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          Mitigation timeline
          {shipmentRequestId && (
            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
              {shipmentRequestId}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}
          {error && (
            <Alert severity="error">Failed to load timeline. The request may not exist.</Alert>
          )}
          {timeline && !isLoading && (
            <Stack spacing={3}>
              {opsGuidance && (
                <Alert severity={opsGuidance.severity}>
                  <Typography variant="subtitle2">{opsGuidance.label}</Typography>
                  <Typography variant="body2">{opsGuidance.detail}</Typography>
                </Alert>
              )}

              <TimelineSummary timeline={timeline} />

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Ops actions
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={1}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<PlayArrowIcon />}
                    onClick={() => mitigateMutation.mutate()}
                    disabled={mitigateMutation.isPending || isGivenUp}
                  >
                    Mitigate now
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<RestartAltIcon />}
                    onClick={() => setResetConfirm(true)}
                    disabled={resetMutation.isPending}
                  >
                    Reset ladder
                  </Button>
                  {outstandingReprice && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="secondary"
                      startIcon={<TrendingUpIcon />}
                      onClick={() => applyPriceMutation.mutate()}
                      disabled={applyPriceMutation.isPending}
                    >
                      Apply approved raise
                    </Button>
                  )}
                  {(isGivenUp || needsCarrierSourcing) && (
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<LocalShippingIcon />}
                      onClick={() => setPlacementOpen(true)}
                    >
                      Manual placement
                    </Button>
                  )}
                  {isGivenUp && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<UndoIcon />}
                      onClick={() => setReturnConfirm(true)}
                    >
                      Return to matching
                    </Button>
                  )}
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteForeverIcon />}
                    onClick={() => setResetToNewConfirm(true)}
                    disabled={resetToNewMutation.isPending}
                  >
                    Reset to NEW
                  </Button>
                </Stack>
              </Box>

              {!isGivenUp && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Extend deadline
                  </Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="flex-start">
                    <TextField
                      size="small"
                      label="Hours"
                      type="number"
                      value={extendHours}
                      onChange={(e) => setExtendHours(e.target.value)}
                      inputProps={{ min: 1, step: 0.5 }}
                      sx={{ width: 100 }}
                    />
                    <TextField
                      size="small"
                      label="Reason (optional)"
                      value={extendReason}
                      onChange={(e) => setExtendReason(e.target.value)}
                      fullWidth
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<ScheduleIcon />}
                      onClick={() => extendMutation.mutate()}
                      disabled={
                        extendMutation.isPending ||
                        !extendHours ||
                        parseFloat(extendHours) <= 0
                      }
                    >
                      Extend
                    </Button>
                  </Stack>
                </Box>
              )}

              <Divider />

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Event history
                </Typography>
                {timeline.events.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No mitigation events recorded yet.
                  </Typography>
                ) : (
                  <Stack spacing={1}>
                    {timeline.events.map((ev) => (
                      <Box
                        key={ev.id}
                        sx={{
                          p: 1.5,
                          borderRadius: 1,
                          bgcolor: 'action.hover',
                          borderLeft: 3,
                          borderColor: ev.eventType === 'GAVE_UP' ? 'error.main' : 'primary.main',
                        }}
                      >
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                          <Typography variant="body2" fontWeight={600}>
                            {EVENT_TYPE_LABELS[ev.eventType] || ev.eventType}
                          </Typography>
                          {ev.rung && (
                            <Chip
                              label={RUNG_LABELS[ev.rung] || ev.rung}
                              size="small"
                              color={rungChipColor(ev.rung)}
                            />
                          )}
                          {ev.actor && (
                            <Chip label={ev.actor} size="small" variant="outlined" />
                          )}
                        </Stack>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {fmtDateTime(ev.occurredAt)}
                        </Typography>
                        {ev.detail && (
                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                            {ev.detail}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Stack>
                )}
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={resetConfirm}
        title="Reset mitigation ladder?"
        description="Clears attempted rungs so every rung becomes available again. Use when circumstances changed — a new depot opened, fleet freed up."
        confirmLabel="Reset ladder"
        severity="warning"
        loading={resetMutation.isPending}
        onConfirm={() => resetMutation.mutate()}
        onClose={() => setResetConfirm(false)}
      />

      <ConfirmDialog
        open={returnConfirm}
        title="Return to matching?"
        description={
          <Stack spacing={2}>
            <Typography variant="body2">
              Clears the give-up flag and puts the request back to NEW for automated matching.
            </Typography>
            <TextField
              size="small"
              label="Reason (optional)"
              value={returnReason}
              onChange={(e) => setReturnReason(e.target.value)}
              fullWidth
            />
          </Stack>
        }
        confirmLabel="Return to matching"
        severity="warning"
        loading={returnMutation.isPending}
        onConfirm={() => returnMutation.mutate()}
        onClose={() => setReturnConfirm(false)}
      />

      {shipmentRequestId && (
        <ManualPlacementDialog
          shipmentRequestId={shipmentRequestId}
          open={placementOpen}
          onClose={() => setPlacementOpen(false)}
          onSuccess={() => {
            setPlacementOpen(false)
            invalidate()
            refetch()
          }}
        />
      )}

      <ConfirmDialog
        open={resetToNewConfirm}
        title="Reset to NEW?"
        description="Heavy recovery: drops all shipments and offers for this request and returns it to NEW. Use only when the request needs a full rematch from scratch."
        confirmPhrase="RESET"
        confirmHelper='Type RESET to confirm'
        confirmLabel="Reset to NEW"
        loading={resetToNewMutation.isPending}
        onConfirm={() => resetToNewMutation.mutate()}
        onClose={() => setResetToNewConfirm(false)}
      />
    </>
  )
}

function TimelineSummary({ timeline }: { timeline: MitigationTimeline }) {
  const shipperLabel =
    timeline.shipperBusinessName || timeline.shipperName || timeline.shipperAccountId || '—'

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <SummaryField label="Shipper" value={shipperLabel} />
        <SummaryField label="Status" value={timeline.status} />
        <SummaryField label="Urgency" value={timeline.urgency} chipColor={urgencyColor(timeline.urgency)} />
        <SummaryField label="Pickup" value={fmtDateTime(timeline.pickupWindowStart)} />
        {timeline.pickupAddress && (
          <SummaryField label="Pickup address" value={timeline.pickupAddress} />
        )}
      </Grid>
      <Grid item xs={12} sm={6}>
        <SummaryField
          label="Time to deadline"
          value={formatHours(timeline.hoursToDeadline)}
          highlight={
            timeline.hoursToDeadline != null && timeline.hoursToDeadline < 2 ? 'error' : undefined
          }
        />
        <SummaryField label="Deadline" value={fmtDateTime(timeline.deadline)} />
        <SummaryField label="Matching attempts" value={timeline.matchingAttemptCount} />
        <SummaryField label="Offer rounds" value={timeline.offerRoundCount} />
        <SummaryField label="Offers sent" value={timeline.jobOffersSentCount} />
        <SummaryField label="Max dispatch round" value={timeline.maxDispatchRound} />
        <SummaryField
          label="Unmatched alert sent"
          value={fmtDateTime(timeline.unmatchedAlertSentAt)}
        />
      </Grid>
      <Grid item xs={12}>
        <Typography variant="caption" color="text.secondary">
          Rungs attempted
        </Typography>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ gap: 0.5, mt: 0.5 }}>
          {(timeline.attemptedMitigationRungs || []).length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              None yet
            </Typography>
          ) : (
            timeline.attemptedMitigationRungs!.map((r) => (
              <Chip key={r} label={RUNG_LABELS[r] || r} size="small" color={rungChipColor(r)} />
            ))
          )}
          {timeline.currentRung && (
            <Chip
              label={`Current: ${RUNG_LABELS[timeline.currentRung] || timeline.currentRung}`}
              size="small"
              variant="outlined"
              color="primary"
            />
          )}
        </Stack>
      </Grid>
      {(timeline.priceEscalationStep != null || timeline.givenUpAt) && (
        <Grid item xs={12}>
          {timeline.priceEscalationStep != null && (
            <Alert severity="info" sx={{ mb: 1 }}>
              Price escalation: proposed step {timeline.priceEscalationStep}
              {timeline.priceEscalationApprovedStep != null &&
                ` · approved step ${timeline.priceEscalationApprovedStep}`}
              {timeline.priceEscalationProposedAt &&
                ` · proposed ${fmtDateTime(timeline.priceEscalationProposedAt)}`}
              {timeline.priceEscalationDecidedAt &&
                ` · decided ${fmtDateTime(timeline.priceEscalationDecidedAt)}`}
            </Alert>
          )}
          {timeline.givenUpAt && (
            <Alert severity="error">
              Gave up {fmtDateTime(timeline.givenUpAt)}
              {timeline.giveUpReason && ` — ${timeline.giveUpReason}`}
            </Alert>
          )}
        </Grid>
      )}
    </Grid>
  )
}

function SummaryField({
  label,
  value,
  highlight,
  chipColor,
}: {
  label: string
  value?: string | number | null
  highlight?: 'error'
  chipColor?: 'default' | 'warning' | 'error'
}) {
  const display = value == null || value === '' ? '—' : String(value)
  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      {chipColor && value ? (
        <Box>
          <Chip label={display} size="small" color={chipColor} />
        </Box>
      ) : (
        <Typography
          variant="body2"
          fontWeight={500}
          color={highlight === 'error' ? 'error.main' : 'text.primary'}
        >
          {display}
        </Typography>
      )}
    </Box>
  )
}

export { RUNG_LABELS, formatHours as formatHoursToDeadline }
